import React, { useEffect, useState } from "react";
import Header from "../components/Header";
import { api, apiErr } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Star, Trash2, Crown, Upload } from "lucide-react";
import { toast } from "sonner";

export default function Owner() {
  const [syllabus, setSyllabus] = useState({});
  const [feedback, setFeedback] = useState([]);
  const [form, setForm] = useState({
    title: "", student_class: "X", subject: "physics", chapter_id: "",
    resource_type: "note", content: "",
  });
  const [chapters, setChapters] = useState([]);

  useEffect(() => {
    api.get("/syllabus").then((r) => setSyllabus(r.data));
    api.get("/feedback").then((r) => setFeedback(r.data));
  }, []);

  useEffect(() => {
    const c = syllabus?.[form.student_class]?.[form.subject] || [];
    setChapters(c);
    if (c.length && !c.find((x) => x.id === form.chapter_id)) {
      setForm((f) => ({ ...f, chapter_id: c[0].id }));
    }
  }, [syllabus, form.student_class, form.subject]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/resources", form);
      toast.success("Resource uploaded!");
      setForm({ ...form, title: "", content: "" });
    } catch (e) { toast.error(apiErr(e)); }
  };

  const avg = feedback.length ? (feedback.reduce((s, f) => s + f.rating, 0) / feedback.length).toFixed(1) : "—";

  return (
    <div className="min-h-screen">
      <Header />
      <div className="max-w-6xl mx-auto px-4 sm:px-8 py-8">
        <div className="flex items-center gap-2 mb-2">
          <Crown size={22} strokeWidth={2.5}/>
          <div className="text-xs font-bold tracking-[0.2em] uppercase">Owner Dashboard</div>
        </div>
        <h1 className="text-4xl font-black tracking-tight mb-6">Manage ScienceLab</h1>

        <div className="grid md:grid-cols-3 gap-4 mb-8">
          <div className="brut-card p-4 bg-[#E9E6FA]"><div className="text-xs font-bold uppercase tracking-widest">Total Feedback</div><div className="text-3xl font-black">{feedback.length}</div></div>
          <div className="brut-card p-4 bg-[#E8F3EC]"><div className="text-xs font-bold uppercase tracking-widest">Average Rating</div><div className="text-3xl font-black">{avg} ⭐</div></div>
          <div className="brut-card p-4 bg-[#FFF5D1]"><div className="text-xs font-bold uppercase tracking-widest">Role</div><div className="text-3xl font-black">Owner</div></div>
        </div>

        <div className="grid lg:grid-cols-2 gap-6">
          {/* Upload */}
          <form onSubmit={submit} className="brut-card p-6 bg-white space-y-3">
            <div className="flex items-center gap-2 mb-2">
              <Upload size={18} strokeWidth={2.5}/>
              <h2 className="text-xl font-extrabold">Add Chapter Resource</h2>
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-widest">Title</Label>
              <Input data-testid="owner-title" required value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                className="brut-input rounded-none mt-1"/>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs font-bold uppercase tracking-widest">Class</Label>
                <select data-testid="owner-class" value={form.student_class}
                  onChange={(e) => setForm({ ...form, student_class: e.target.value })}
                  className="brut-input rounded-none mt-1 w-full px-2 py-2">
                  <option value="IX">IX</option><option value="X">X</option>
                </select>
              </div>
              <div>
                <Label className="text-xs font-bold uppercase tracking-widest">Subject</Label>
                <select data-testid="owner-subject" value={form.subject}
                  onChange={(e) => setForm({ ...form, subject: e.target.value })}
                  className="brut-input rounded-none mt-1 w-full px-2 py-2">
                  <option value="physics">Physics</option><option value="chemistry">Chemistry</option>
                </select>
              </div>
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-widest">Chapter</Label>
              <select data-testid="owner-chapter" value={form.chapter_id}
                onChange={(e) => setForm({ ...form, chapter_id: e.target.value })}
                className="brut-input rounded-none mt-1 w-full px-2 py-2">
                {chapters.map((c) => <option key={c.id} value={c.id}>{c.num}. {c.title}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-widest">Type</Label>
              <select data-testid="owner-type" value={form.resource_type}
                onChange={(e) => setForm({ ...form, resource_type: e.target.value })}
                className="brut-input rounded-none mt-1 w-full px-2 py-2">
                <option value="note">Note/Text</option><option value="link">Link (URL)</option>
              </select>
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-widest">
                {form.resource_type === "link" ? "URL" : "Content"}
              </Label>
              <Textarea data-testid="owner-content" required value={form.content}
                onChange={(e) => setForm({ ...form, content: e.target.value })}
                className="brut-input rounded-none mt-1 min-h-[120px]"/>
            </div>
            <Button type="submit" data-testid="owner-submit"
              className="brut-btn rounded-none w-full bg-[#1A1A1A] text-white hover:bg-[#1A1A1A]">
              Upload Resource
            </Button>
          </form>

          {/* Feedback */}
          <div className="brut-card p-6 bg-white">
            <h2 className="text-xl font-extrabold mb-3">User Feedback</h2>
            {feedback.length === 0 ? (
              <div className="text-neutral-600 py-8 text-center">
                <div className="font-bold">No feedback yet</div>
                <div className="text-sm">Users&apos; feedback will appear here.</div>
              </div>
            ) : (
              <div className="space-y-3 max-h-[500px] overflow-y-auto pr-1">
                {feedback.map((f) => (
                  <div key={f.id} className="brut-border p-3 bg-[#FAF9F6]" data-testid={`feedback-${f.id}`}>
                    <div className="flex items-center justify-between">
                      <div className="font-bold">{f.user_name || f.user_email}</div>
                      <div className="flex gap-0.5">
                        {[1,2,3,4,5].map(n =>
                          <Star key={n} size={14} strokeWidth={2.5} fill={n <= f.rating ? "#1A1A1A" : "none"}/>
                        )}
                      </div>
                    </div>
                    {f.comment && <div className="text-sm mt-1">{f.comment}</div>}
                    <div className="text-[10px] text-neutral-500 mt-1">{new Date(f.created_at).toLocaleString()}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
